import React from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import EnquiryForm from "@/components/EnquiryForm";
import { useSite } from "@/context/SiteContext";

export default function About() {
  const { settings } = useSite();
  const s = settings || {};
  const wa = s.whatsapp || "919876543210";

  return (
    <div data-testid="about-page">
      <Seo
        title="About & Contact — Mayuresh Caterers, Roha, Raigad 402116"
        description="Family-run catering in Roha, Raigad since our first wedding. Contact Mayuresh Caterers for wedding, corporate and private event catering. Phone, address and enquiry form."
      />

      <section className="bg-[#1A1A1A] py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="label-tag with-rule text-[#C9A66B] mb-5">Our Story</p>
            <h1 className="text-white text-5xl sm:text-6xl font-medium leading-tight display-title">Rooted in Roha,<br />served with <span className="italic text-gold-grad">heart</span></h1>
            <p className="text-white/70 mt-6 leading-relaxed text-lg">
              Mayuresh Caterers began with a simple belief — that good food brings people closer.
              For years we have catered weddings, corporate gatherings and family functions across
              Roha and the wider Raigad district, blending authentic Maharashtrian flavours with
              refined, modern presentation.
            </p>
            <p className="text-white/70 mt-4 leading-relaxed">
              Every event is handled by one dedicated team, from the first tasting to the last plate cleared.
            </p>
          </div>
          <img src="https://images.pexels.com/photos/36916402/pexels-photo-36916402.jpeg" alt="Mayuresh Caterers premium buffet setup" className="rounded-sm w-full h-[420px] object-cover" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-24 grid lg:grid-cols-2 gap-14">
        <div>
          <p className="label-tag with-rule text-[#B08D4F] mb-5">Get in Touch</p>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1A1A] display-title">Request a quote</h2>
          <p className="text-[#5b554b] mt-4 leading-relaxed">
            Share your event details and we'll get back with a personalised quote. Prefer chatting? Reach us on WhatsApp.
          </p>

          <div className="mt-8 space-y-5">
            <div className="flex gap-4 items-start"><MapPin className="text-[#C9A66B] shrink-0" /><p className="text-[#1A1A1A]">{s.address || "Market Road, Roha, Raigad, Maharashtra 402116"}</p></div>
            <div className="flex gap-4 items-start"><Phone className="text-[#C9A66B] shrink-0" /><a href={`tel:${(s.phone||"").replace(/\s/g,"")}`} className="text-[#1A1A1A] hover:text-[#B08D4F]">{s.phone || "+91 98765 43210"}</a></div>
            <div className="flex gap-4 items-start"><Mail className="text-[#C9A66B] shrink-0" /><a href={`mailto:${s.email}`} className="text-[#1A1A1A] hover:text-[#B08D4F]">{s.email || "hello@mayuresh.in"}</a></div>
            <div className="flex gap-4 items-start"><Clock className="text-[#C9A66B] shrink-0" /><p className="text-[#1A1A1A]">Open daily · 9:00 AM – 9:00 PM</p></div>
          </div>

          <a href={`https://wa.me/${wa}?text=${encodeURIComponent("Hi Mayuresh Caterers, I'd like a catering quote.")}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-8 bg-[#25D366] text-white px-6 py-3 rounded-sm text-sm font-semibold hover:brightness-95 transition-all"
            data-testid="whatsapp-contact-btn">
            <MessageCircle size={18} /> Chat on WhatsApp
          </a>
        </div>

        <div className="card-lux rounded-sm p-8 sm:p-10">
          <EnquiryForm />
        </div>
      </section>
    </div>
  );
}
